import React, { useEffect, useState } from "react";
import axios from "axios";
import baseURL from "../../config";
import { useParams } from "react-router-dom";

// MUI imports
import {
    Avatar,
    Typography,
    Chip,
    Box,
    Card,
    CardContent,
    Skeleton,
    Fade,
    Container,
    Button,
    Grid,
    TextField,
    InputAdornment,
} from "@mui/material";
import {
    LocationOnRounded,
    AttachMoneyRounded,
    CalendarTodayRounded,
    BusinessRounded,
    WorkRounded,
    ScheduleRounded,
    SearchRounded,
} from "@mui/icons-material";

const VacancyList = () => {
    const [listOfVacancies, setListOfVacancies] = useState([]);
    const [filteredVacancies, setFilteredVacancies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const { id } = useParams();
    const userId = localStorage.getItem("urlID");

    const fetchVacancyList = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(
                `${baseURL}/api/vacancy/postedby/${userId}`,
                {
                    headers: { Authorization: token },
                }
            );
            console.log("response", response.data.data);
            setListOfVacancies(response.data.data);
            setFilteredVacancies(response.data.data);
        } catch (error) {
            console.error("Error fetching vacancies:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVacancyList();
    }, []);

    // Filter vacancies based on rank search
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredVacancies(listOfVacancies);
        } else {
            const filtered = listOfVacancies.filter(vacancy =>
                vacancy.rank.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredVacancies(filtered);
        }
    }, [searchTerm, listOfVacancies]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Box sx={{ mb: 4 }}>
                    <Skeleton variant="text" width={200} height={40} />
                    <Skeleton variant="text" width={300} height={20} sx={{ mt: 1 }} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {[...Array(3)].map((_, index) => (
                        <Card key={index} sx={{ borderRadius: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                                    <Skeleton variant="circular" width={60} height={60} />
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton variant="text" width="60%" height={24} />
                                        <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
                                    </Box>
                                    <Skeleton variant="rectangular" width={60} height={24} />
                                </Box>
                                <Skeleton variant="text" width="100%" height={20} />
                                <Skeleton variant="text" width="80%" height={20} sx={{ mt: 1 }} />
                                <Box sx={{ mt: 2 }}>
                                    <Skeleton variant="rectangular" width="100%" height={48} />
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Fade in timeout={600}>
                <Box>
                    {/* Header Section */}
                    <Box
                        sx={{
                            mb: 4,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between"
                        }}
                    >
                        {/* Search Bar (left) */}
                        <TextField
                            placeholder="Search by rank (e.g., Chief Engineer, Captain, etc.)"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchRounded sx={{ color: '#666' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                maxWidth: 350,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    backgroundColor: '#f8f9fa',
                                    '&:hover': { backgroundColor: '#f1f3f4' },
                                    '&.Mui-focused': { backgroundColor: 'white' },
                                },
                            }}
                        />

                        {/* Vacancy List (center) */}
                        <Typography
                            variant="h4"
                            fontWeight="700"
                            sx={{
                                color: '#d32f2f',
                                textAlign: "center",
                                flex: 1
                            }}
                        >
                            Vacancy List
                        </Typography>

                        {/* Posted Vacancies (right) */}
                        <Typography
                            variant="h6"
                            color="text.secondary"
                            fontWeight="400"
                            sx={{ minWidth: "200px", textAlign: "right" }}
                        >
                            {filteredVacancies.length} Posted Vacancies
                        </Typography>
                    </Box>

                    {/* Vacancy Cards */}
                    <Grid container spacing={2}>
                        {filteredVacancies.length > 0 ? (
                            filteredVacancies.map((vacancy) => (
                                <Grid item xs={12} sm={6} key={vacancy._id}>
                                    <Card
                                        sx={{
                                            borderRadius: 3,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                            border: '1px solid #e0e0e0',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                                transform: 'translateY(-2px)',
                                                transition: 'all 0.3s ease',
                                            }
                                        }}
                                    >
                                        {/* Red header bar */}
                                        <Box sx={{
                                            height: 6,
                                            background: 'linear-gradient(90deg, #d32f2f 0%, #f44336 100%)'
                                        }} />

                                        <CardContent sx={{ p: 2.5 }}>
                                            {/* Header with Avatar, Title, and oiler badge */}
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                                <Avatar
                                                    src={`${baseURL}${vacancy.img?.filePath}`}
                                                    alt={vacancy.title}
                                                    sx={{
                                                        width: 50,
                                                        height: 50,
                                                        backgroundColor: '#f5f5f5'
                                                    }}
                                                >
                                                    <WorkRounded sx={{ color: '#666', fontSize: 20 }} />
                                                </Avatar>

                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight="600"
                                                        sx={{
                                                            color: '#333',
                                                            mb: 0.5,
                                                            fontSize: '1.1rem'
                                                        }}
                                                    >
                                                        {vacancy.title}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <BusinessRounded sx={{ fontSize: 14, color: '#666' }} />
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                            {vacancy.postedBy?.name || 'Company Name'}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Chip
                                                    label={vacancy?.isActive ? "Active" : "Deactive"}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: vacancy?.isActive ? "#28a745" : "#dc3545", // green for active, red for deactive
                                                        color: "white",
                                                        fontWeight: 500,
                                                        fontSize: "0.7rem",
                                                        height: 24,
                                                    }}
                                                />

                                            </Box>

                                            {/* Job Details */}
                                            <Box sx={{ mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                    <WorkRounded sx={{ fontSize: 16, color: '#333' }} />
                                                    <Typography variant="body1" fontWeight="600" color="#333" sx={{ fontSize: '0.95rem' }}>
                                                        {vacancy.rank}
                                                    </Typography>
                                                </Box>

                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                            <AttachMoneyRounded sx={{ fontSize: 16, color: '#ff9800' }} />
                                                            <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.85rem' }}>
                                                                {vacancy.salary
                                                                    ? `${vacancy.salary.min} - ${vacancy.salary.max} ${vacancy.salary.currency}`
                                                                    : "Salary not specified"}
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LocationOnRounded sx={{ fontSize: 16, color: '#d32f2f' }} />
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                                {vacancy.location}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={12} sm={6}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                            <CalendarTodayRounded sx={{ fontSize: 16, color: '#ff9800' }} />
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                                {new Date(vacancy.joiningDate).toLocaleDateString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    year: 'numeric'
                                                                })}
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <ScheduleRounded sx={{ fontSize: 16, color: '#d32f2f' }} />
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                                {vacancy.contractDuration}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>

                                            {/* Requirements Tags */}
                                            {vacancy.requirements && vacancy.requirements.length > 0 && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {vacancy.requirements.slice(0, 4).map((req, idx) => (
                                                            <Chip
                                                                key={idx}
                                                                label={req}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: '#f5f5f5',
                                                                    color: '#666',
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: 500,
                                                                    height: 22,
                                                                }}
                                                            />
                                                        ))}


                                                    </Box>
                                                </Box>
                                            )}

                                            {/* Apply Now Button */}
                                            {/* <Button
                                                variant="contained"
                                                fullWidth
                                                sx={{
                                                    backgroundColor: '#C30B12',
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    py: 1,
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    fontSize: '0.9rem',
                                                }}
                                            >
                                                Apply Now
                                            </Button> */}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))
                        ) : (
                            <Card sx={{ borderRadius: 3, textAlign: 'center', py: 4 }}>
                                <CardContent>
                                    <WorkRounded sx={{ fontSize: 50, color: '#ccc', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" fontWeight="500" gutterBottom>
                                        {searchTerm ? `No vacancies found for "${searchTerm}"` : "No vacancies found"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {searchTerm ? "Try searching with a different rank" : "Your posted job vacancies will appear here"}
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                    </Grid> {/* ✅ Correct closing tag */}
                </Box>
            </Fade>
        </Container>
    );
};

export default VacancyList;